import { Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { UseFormReset, UseFormGetValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import {
  Button,
  FillForm,
  FillProducts,
  FillVariations,
  ITemplateSideGroup,
  ITemplateTable,
  ITemplateVariation,
  ThemeContext,
} from '@forma/forma-ui-kit';
// import ConteragentsSearch from '@/components/views/ConteragentsSearch';
import { useLazyQuery } from '@/hooks/useQuery';
import { queries } from '@/helpers/api';
import { MainContext } from '@/context/MainContext';
import ConteragentsSearch from '../ConteragentsSearch';
import FillSideTable from './FillSideTable';
import FillFromCardButton from './FillFromCardButton';

import {
  ISideEditData,
  ISidesListEditData,
} from '@/helpers/interfaces/templates.interface';
import { ICounteragent } from '@/helpers/interfaces/counteragents.interface';

import styles from './fill-document.module.css';

interface FillDocumentSideProps {
  id: string,
  name: string,
  tattrFolders: ITemplateSideGroup[],
  tables: ITemplateTable[],
  carticles: ITemplateVariation[],

  sideData?: ISideEditData | null,
  onChangeSidesData: Dispatch<SetStateAction<ISidesListEditData | undefined>>,
  onClickChangeMapping?: () => void,
  defaultValues?: ISideEditData,
}

interface FormRef {
  reset: UseFormReset<{ [key: string]: string }>
  getValues: UseFormGetValues<{ [key: string]: string }>,
  setValue: UseFormSetValue<{ [key: string]: string }>,
  revalidate: UseFormTrigger<{ [key: string]: string }>,
}

const clearArticleValue = (carticles: ITemplateVariation[], articleValues: ISideEditData['articleValues'], vid: string) => {
  let next = [...articleValues];
  const carticle = carticles.find(article => article.id === vid);

  const articleIndex = next.findIndex(article => article.id === vid);
  if (articleIndex !== -1) next[articleIndex].value = [];

  carticle?.variants.forEach(({ id }) => {
    const childCarticle = carticles.find(({ parents }) => parents.includes(id));
    if (childCarticle) next = clearArticleValue(carticles, next, childCarticle.id);
  });
  return next;
};

export const clearSideDataByCarticleId = (
  sideData: ISideEditData, carticles: ITemplateVariation[], vid: string, defaultValues?: ISideEditData
): ISideEditData => {
  const articleValues = clearArticleValue(carticles, sideData.articleValues, vid);

  const tablesInCarticle: string[] = [];
  const tattrsInCarticle: string[] = [];

  const carticle = carticles.find(article => article.id === vid);
  carticle?.variants.forEach(({ tables, tattrs }) => {
    tablesInCarticle.push(...tables);
    tattrsInCarticle.push(...tattrs);
  });

  const tattrValues = { ...sideData.tattrValues };
  tattrsInCarticle?.forEach(id => {
    const simpleVariableId = id.split('.')[0];
    if (defaultValues?.tattrValues[simpleVariableId]) tattrValues[simpleVariableId] = defaultValues?.tattrValues[simpleVariableId];
    else if (tattrValues[id]) delete (tattrValues[id]);
  });

  const tableValues = sideData.tableValues?.filter(({ tableId }) => !tablesInCarticle.includes(tableId));

  return {
    ...sideData,
    articleValues,
    tattrValues,
    tableValues
  };
};

const setArticleValue = (prevValues: ISideEditData['articleValues'], vid: string, ids: string[]) => {
  const articleValues = [...prevValues];
  const index = articleValues.findIndex(item => item.id === vid);
  if (index === -1) articleValues.push({ id: vid, value: ids });
  else articleValues[index] = { id: vid, value: ids };
  return articleValues;
};

const FillDocumentSide: FC<FillDocumentSideProps> = ({
  id,
  tattrFolders,
  tables,
  carticles,
  sideData,
  onChangeSidesData,
  onClickChangeMapping,
  defaultValues
}) => {
  const { t } = useTranslation();
  const { lang } = useContext(ThemeContext);
  const { token } = useContext(MainContext);
  const formRef = useRef<FormRef | null>(null);
  const [isShowCounteragents, setShowCounteragents] = useState<boolean>(false);
  const [isNeedReset, setNeedReset] = useState(false);

  const { mutate: getCounteragent } = useLazyQuery<ICounteragent>(queries.getCounteragentById);

  useEffect(() => {
    if (!isNeedReset) return;
    if (defaultValues?.tattrValues) resetValues(defaultValues?.tattrValues);
    setNeedReset(false);
    // eslint-disable-next-line
  }, [isNeedReset, defaultValues])

  const handleSetVariations = (vid: string, ids: string[]) => {
    onChangeSidesData(prev => {
      if (!prev) return prev;

      let articleValues = setArticleValue(prev[id].articleValues, vid, ids);
      const sideData = clearSideDataByCarticleId(prev[id], carticles, vid);

      if (ids.length === 0 && carticles.length > 1) {
        const flatVariantsCurrentCarticle = carticles.find(({ id }) => id === vid)?.variants.map(({ id }) => id);
        carticles.forEach(({ id, parents }) => {
          const isCurrentVariantParent = !!parents.find(parentId => flatVariantsCurrentCarticle?.includes(parentId));
          if (isCurrentVariantParent) articleValues = setArticleValue(articleValues, id, []);
        });
      };

      return ({ ...prev, [id]: { ...sideData, articleValues } });
    });

    setNeedReset(true);
  };

  const handleChangeValid = (valid: boolean) => {
    onChangeSidesData(prev => prev && ({ ...prev, [id]: { ...prev[id], isValid: valid } }));
  };

  const handleSetCounteragent = async (cid: string) => {
    const data = await getCounteragent(cid, lang, token);
    if (data) {
      setShowCounteragents(false);
      const nameArr = data && [data.attrValues[101], data.attrValues[102], data.attrValues[103]];
      onChangeSidesData(prev => prev && ({ ...prev, [id]: { ...prev[id], counteragentid: cid, counteragentname: data && (data.attrValues[304] || nameArr.join(' ')) } }));
      resetValues(data.attrValues);
      return data;
    } return null;
  };

  const handleSetUpdate = (checked: boolean) => {
    onChangeSidesData(prev => prev && ({ ...prev, [id]: { ...prev[id], updateCounteragent: checked } }));
  };

  const handleChangeForm = (tattrValues: { [key: string]: string }) => {
    onChangeSidesData(prev => prev && ({ ...prev, [id]: { ...prev[id], tattrValues } }));
  };

  const handleErrorForm = (tattrErrors: { [key: string]: string }) => {
    onChangeSidesData(prev => prev && ({ ...prev, [id]: { ...prev[id], tattrErrors } }));
  };

  const handleSetProducts = (
    tableId: string,
    products: { [key: string]: string }[], total: { [key: string]: string },
    customProductsIds: string[],
    excludeVAT: boolean
  ) => {
    onChangeSidesData(prev => {
      if (!prev) return prev;
      const tableValues = [...prev[id].tableValues];
      const index = tableValues?.findIndex(table => table.tableId === tableId);
      if (!tableValues || index === -1) tableValues.push({ tableId, products, total, customProductsIds, excludeVAT });
      else tableValues[index] = { tableId, products, total, customProductsIds, excludeVAT };
      return ({ ...prev, [id]: { ...prev[id], tableValues } });
    });
  };

  const resetValues = (data: { [key: string]: string }) => {
    const faltTattrs = tattrFolders?.flatMap(({ tattrs }) => tattrs);

    if (!faltTattrs || !formRef.current) return;

    const values = formRef.current.getValues();
    const newValues: { [key: string]: string } = {};

    Object.keys(values).forEach(key => {
      if (data[key] && faltTattrs.find(({ id }) => id === key)) newValues[key] = data[key];
      else newValues[key] = '';
    });

    formRef.current?.reset(newValues);
    setTimeout(() => formRef.current?.revalidate(), 1000);
  };

  return (
    <div>
      {!!(tattrFolders.length && onClickChangeMapping) && (
        <div className={styles.head}>
          <FillFromCardButton
            open={isShowCounteragents}
            selectedId={sideData?.counteragentid}
            onClick={() => setShowCounteragents(!isShowCounteragents)}
            onCheck={checked => handleSetUpdate(checked)}
            selectedName={sideData?.counteragentname}
            className={classNames(styles.button, styles.secondary)}
          />
          {onClickChangeMapping && (
            <Button
              viewStyle="text"
              className={classNames(styles.button, styles.textButton, styles.mappingButton)}
              iconClassName={styles.buttonIcon}
              icon={<ReactSVG src="/icons/settings.svg" wrapper="span" />}
              onClick={onClickChangeMapping}
              reversed
            >
              {t('change_variable_mapping')}
            </Button>
          )}
        </div>
      )}
      {isShowCounteragents && (
        <div className={styles.counteragents}>
          <ConteragentsSearch
            onClickItem={(counteragent) => handleSetCounteragent(counteragent.id)}
          />
        </div>
      )}
      {!!(sideData && carticles?.length) && (
        <div className={styles.variations}>
          {sideData && carticles?.map((item) => (
            <FillVariations
              {...item}
              value={sideData.articleValues?.find(({ id }) => id === item.id)?.value ?? []}
              onChange={(ids) => handleSetVariations(item.id, ids)}
              key={item.id}
            />
          ))}
        </div>
      )}
      <div className={styles.fillingFields}>
        <div style={{ display: isShowCounteragents ? 'none' : 'block' }}>
          <FillForm
            values={sideData ? sideData.tattrValues : {}}
            folders={tattrFolders}
            onChange={values => handleChangeForm(values)}
            onErrors={errors => handleErrorForm(errors)}
            onChangeValid={valid => handleChangeValid(valid)}
            showGroupName
            showCount
            ref={formRef}
          />
        </div>
        {!!tables?.length && tables.map((table) => {
          const tableValues = sideData?.tableValues.find(({ tableId }) => tableId === table.id);
          return (
            <div className="fill-products-wrapper" key={table.id}> {/* class for seo styling */}
              <FillSideTable
                data={table}
                products={tableValues?.products ?? []}
                productsTotal={tableValues?.total ?? {}}
                customProductsIds={tableValues?.customProductsIds ?? []}
                excludeVAT={tableValues?.excludeVAT ?? false}
                onChangeTable={(products, total, customProductsIds, excludeVAT) => {
                  handleSetProducts(table.id, products, total, customProductsIds, excludeVAT);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FillDocumentSide;
